import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCircle } from "react-icons/fa";
import { MdOutlineViewTimeline } from "react-icons/md";

import user_placeholder from "../../../assets/user-profile.jpg";
import classroomServices from "../../../services/classroomServices";
import Button from "../../Button";
import Modal from "../../Modal";

const ClassroomTimelineModal = forwardRef(({ onSubmit }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [classroomTimeline, setClassroomTimeline] = useState([]);
  const [classroomID, setClassroomID] = useState("");

  useImperativeHandle(ref, () => ({
    openModal: (classroom) => {
      setClassroomID(classroom?.classroom?.id);
      setIsOpen(true);
    },
    closeModal: () => {
      setIsOpen(false);
    },
  }));

  const getTimeline = async () => {
    try {
      const response = await classroomServices.getClassroomTimeline(classroomID);
      setClassroomTimeline(response);
    } catch (error) {
      console.error("Error fetching classroom timeline:", error);
      setClassroomTimeline([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getTimeline();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      header={
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-blue-500">
            <MdOutlineViewTimeline size={20} />
            {t("classroom_timeline")}
          </p>
        </div>
      }
      footer={
        <div className="sm:space-x-2">
          <Button color="white" variant="outlined" onClick={() => setIsOpen(false)} label={t("close")} />
        </div>
      }
    >
      <div className="relative p-4">
        {/* Vertical line */}
        <div className="absolute left-9 top-0 h-full w-0.5 bg-[#E5E7EB] dark:bg-gray-600"></div>

        {classroomTimeline.length > 0 ? (
          classroomTimeline.map((event, index) => (
            <div key={index} className="relative mb-6 flex items-start gap-4">
              {/* Timeline Indicator */}
              <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full bg-blue-200 text-white">
                <img
                  src={user_placeholder}
                  className="h-8 w-8 rounded-full"
                  alt={event.teacher?.name || "user"}
                />
              </div>

              {/* Event Content */}
              <div className="flex w-full flex-col gap-1 rounded-lg bg-gray-100 p-3 shadow-md dark:bg-gray-800">
                <p className="flex items-center text-lg font-semibold dark:text-gray-100">
                  {event.teacher?.name}{" "}
                  {event?.teacher?.is_deleted && (
                    <span className="ml-2 rounded-full border border-red-500 px-2 text-xs text-red-500">
                      {t("teacher_is_deleted")}
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <FaCircle className="text-xs text-blue-500" />
                  <p>{t("classroom_assigned_at")}:</p>
                  <span className="font-extrabold text-text dark:text-white">{event.created_at}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500">{t("no_timeline_data")}</p>
        )}
      </div>
    </Modal>
  );
});

export default ClassroomTimelineModal;
